import { EyeFilled, EyeInvisibleOutlined } from '@ant-design/icons';
import { Button, Form, Input, Space } from 'antd';
import { Rule } from 'antd/lib/form';
import { useTranslation } from 'react-i18next';
import { validatePassword } from 'all-in-one-shared-api';
import { BoldLabelFormItem } from '../../../components/form/BoldLabelFormItem';
import { useEffect } from 'react';
import { revalidateAntdFormLanguageChanged } from '../../../i18n';

export interface ChangePasswordFormData {
  currentPassword: string;
  newPassword: string;
}

interface ChangePasswordFormProps {
  handleOnCancel: (value: boolean) => void;
  isLoading?: boolean;
  handleOnFinish: (value: ChangePasswordFormData) => void;
}

export const ChangePasswordForm = ({ handleOnCancel, isLoading, handleOnFinish }: ChangePasswordFormProps) => {
  const { t, i18n } = useTranslation();
  const [form] = Form.useForm();

  useEffect(() => {
    revalidateAntdFormLanguageChanged(form);
  }, [i18n.language, form]);

  const commonRules: Rule[] = [
    {
      required: true,
      message: t('updatePassword.error.required'),
    },
  ];

  const passwordRules: Rule[] = [
    ...commonRules,
    () => ({
      validator(_, value) {
        const validationResult = validatePassword(value);

        if (!value || validationResult.isValid) {
          return Promise.resolve();
        }

        return Promise.reject(new Error(t('updatePassword.error.invalidPassword')));
      },
    }),
  ];

  return (
    <Form layout={'vertical'} form={form} onFinish={handleOnFinish} requiredMark={false} validateTrigger={false}>
      <BoldLabelFormItem
        label={t('changePassword.currentPasswordLabel')}
        name="currentPassword"
        hasFeedback
        rules={commonRules}
      >
        <Input.Password
          size="middle"
          style={{ width: 240 }}
          placeholder={t('changePassword.currentPasswordTextPlaceholder')}
          iconRender={(visible) => (visible ? <EyeFilled /> : <EyeInvisibleOutlined />)}
        />
      </BoldLabelFormItem>

      <BoldLabelFormItem
        label={t('changePassword.newPasswordLabel')}
        name="newPassword"
        hasFeedback
        rules={passwordRules}
      >
        <Input.Password
          size="middle"
          style={{ width: 240 }}
          placeholder={t('changePassword.newPasswordTextPlaceholder')}
          iconRender={(visible) => (visible ? <EyeFilled /> : <EyeInvisibleOutlined />)}
        />
      </BoldLabelFormItem>

      <BoldLabelFormItem
        label={t('changePassword.confirmPasswordLabel')}
        name="confirmPassword"
        hasFeedback
        rules={[
          ...passwordRules,
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue('newPassword') === value) {
                return Promise.resolve();
              }
              return Promise.reject(new Error(t('changePassword.error.invalidConfirmPassword')));
            },
          }),
        ]}
      >
        <Input.Password
          size="middle"
          style={{ width: 240 }}
          placeholder={t('changePassword.confirmPasswordTextPlaceholder')}
          iconRender={(visible) => (visible ? <EyeFilled /> : <EyeInvisibleOutlined />)}
        />
      </BoldLabelFormItem>

      <BoldLabelFormItem>
        <Space direction="horizontal">
          <Button block type="primary" loading={isLoading} size="small" htmlType="submit">
            {t('changePassword.submitButton')}
          </Button>
          <Button block type="default" size="small" htmlType="reset" onClick={() => handleOnCancel(false)}>
            {t('common.cancel')}
          </Button>
        </Space>
      </BoldLabelFormItem>
    </Form>
  );
};
